import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, getAccessToken, setTokens } from '@/api/client';
import { AuthContext, type AuthContextValue, type AuthUser } from './AuthContext';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get<AuthUser>('/auth/me');
      setUser(res.data);
    } catch {
      setTokens(null, null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const login = useCallback(async (identifier: string, password: string) => {
    const res = await api.post('/auth/login', { identifier, password });
    setTokens(res.data.accessToken, res.data.refreshToken);
    setUser(res.data.user);
    return res.data.user as AuthUser;
  }, []);

  const logout = useCallback(async () => {
    const refresh = localStorage.getItem('grhamoy.refreshToken');
    try {
      if (refresh) await api.post('/auth/logout', { refreshToken: refresh });
    } catch {
      // ignore
    }
    setTokens(null, null);
    setUser(null);
  }, []);

  const hasPerm = useCallback(
    (moduleId: string) => {
      if (!user) return false;
      if (user.roleCode === 'SUPER_ADMIN') return true;
      return Boolean(user.perms?.[moduleId]);
    },
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, login, logout, refreshMe, hasPerm }),
    [user, loading, login, logout, refreshMe, hasPerm],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
